import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  InvoicingInvoiceStatus,
  InvoicingPaymentMethods,
} from "@/types/invoicing";
import Stripe from "stripe";

interface StripeInvoiceResponse {
  success: boolean;
  stripe_invoice: any;
  local_invoice: any;
}

interface CreateStripeInvoiceArgs {
  patient_id: string;
  service_id: string;
  payment_method: InvoicingPaymentMethods;
}

interface UpdateInvoiceRequest {
  invoiceId: string;
  status: InvoicingInvoiceStatus;
  paymentMethod: InvoicingPaymentMethods;
}

interface Invoice {
  id: string;
  status: InvoicingInvoiceStatus;
  payment_method: InvoicingPaymentMethods;
  stripe_invoice_id: string;
  patient_id: string;
  patient_name: string;
  patient_photo_url: string;
  service: string;
  date: string;
  price: number;
  pdf_url: string;
}

export const invoicesApi = createApi({
  reducerPath: "invoicesApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  tagTypes: ["Invoice"],
  endpoints: (build) => ({
    getInvoices: build.query<
      any,
      {
        status?: string;
        search?: string;
        page?: number;
        perPage?: number;
        dateRange?: string;
        patient_id?: string;
      }
    >({
      query: ({
        status,
        search,
        page = 1,
        perPage = 8,
        dateRange,
        patient_id,
      }) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (dateRange) params.append("dateRange", dateRange);
        params.append("page", page.toString());
        params.append("perPage", perPage.toString());
        if (patient_id) params.append("patient_id", patient_id);
        return `/invoicing?${params.toString()}`;
      },
      providesTags: (result) =>
        result ? [{ type: "Invoice", id: "LIST" }] : [],
    }),
    generateDownloadInvoicePdf: build.mutation<
      Blob,
      {
        status?: string;
        search?: string;
        page?: number;
        perPage?: number;
        dateRange?: string;
      }
    >({
      query: ({ status, search, page = 1, perPage = 8, dateRange }) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (dateRange) params.append("dateRange", dateRange);
        params.append("page", page.toString());
        params.append("perPage", perPage.toString());

        return {
          url: `/stripe/invoices/download?${params.toString()}`,
          responseHandler: async (response) => response.blob(),
        };
      },
    }),

    generateInvoicePdf: build.mutation<
      Stripe.Response<Stripe.Invoice>,
      { invoiceId: string }
    >({
      query: ({ invoiceId }) => ({
        url: `/stripe/invoices?invoice_id=${invoiceId}`,
        method: "GET",
      }),
    }),
    createStripeInvoice: build.mutation<
      StripeInvoiceResponse,
      CreateStripeInvoiceArgs
    >({
      query: (body) => ({
        url: "/stripe/invoices",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Invoice", id: "LIST" }],
    }),
    updateInvoice: build.mutation<Invoice, UpdateInvoiceRequest>({
      query: (body) => ({
        url: "/invoicing",
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "Invoice", id: "LIST" }],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useCreateStripeInvoiceMutation,
  useUpdateInvoiceMutation,
  useGenerateInvoicePdfMutation,
  useGenerateDownloadInvoicePdfMutation,
} = invoicesApi;
